/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { memo, useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { DisplayFromFHIRRelatedArtifact } from "../datatypeComponents/RelatedArtifactEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import BooleanEntry, { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { getRatioDisplay } from "../datatypeComponents/DosageEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { displayStringFromFHIRTiming } from "../datatypeComponents/TimingEntry";
import { getDateDisplay, getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import { DisplayFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import { DisplayFromFHIRCodeableReference } from "../datatypeComponents/CodeableReferenceEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import StringEntry from "../datatypeComponents/StringEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";

const planDefinitionDotAsNeededCodeableConceptSystemChoices = [{ 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' }];
const planDefinitionDotSubjectReferenceResourceTypes = ['Group', 'MedicinalProductDefinition', 'SubstanceDefinition', 'AdministrableProductDefinition', 'ManufacturedItemDefinition', 'PackagedProductDefinition'];
const planDefinitionDotSubjectCodeableConceptValueSet = [
    { system: "http://hl7.org/fhir/fhir-types", code: "CareTeam", display: "CareTeam" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Device", display: "Device" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Group", display: "Group" },
    { system: "http://hl7.org/fhir/fhir-types", code: "HealthcareService", display: "HealthcareService" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Location", display: "Location" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Organization", display: "Organization" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Patient", display: "Patient" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Practitioner", display: "Practitioner" },
    { system: "http://hl7.org/fhir/fhir-types", code: "PractitionerRole", display: "PractitionerRole" },
    { system: "http://hl7.org/fhir/fhir-types", code: "RelatedPerson", display: "RelatedPerson" }
];
const planDefinitionDotSubjectAllowedDatatypes = ['CodeableConcept', 'Reference', 'canonical'];
const planDefinitionDotAsNeededAllowedDatatypes = ['boolean', 'CodeableConcept'];
const planDefinitionDotTypeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/plan-definition-type", code: "order-set", display: "Order Set" },
    { system: "http://terminology.hl7.org/CodeSystem/plan-definition-type", code: "clinical-protocol", display: "Clinical Protocol" },
    { system: "http://terminology.hl7.org/CodeSystem/plan-definition-type", code: "eca-rule", display: "ECA Rule" },
    { system: "http://terminology.hl7.org/CodeSystem/plan-definition-type", code: "workflow-definition", display: "Workflow Definition" }
];

const generateNarrativeForPlanDefinitionResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p><br/>";
        }
        if (resource.subtitle && typeof resource.type === "string") {
            innerDiv += "<p><b>Subtitle: </b>" + resource.subtitle + "</p><br/>";
        }
        if (resource.name) {
            innerDiv += "<p><b>Name: </b>" + resource.name + "</p><br/>";
        }
        if (resource.description) {
            innerDiv += "<p><b>Description: </b>" + resource.description + "</p><br/>";
        }
        if (resource.subjectCodeableConcept) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRCodeableConcept(resource.subjectCodeableConcept) + "</p><br/>";
        }
        if (resource.subjectReference) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(resource.subjectReference) + "</p><br/>";
        }
        if (resource.subjectCanonical) {
            innerDiv += "<p><b>Subject: </b>" + resource.subjectCanonical + "</p><br/>";
        }
        if (resource.action?.length > 0) {
            innerDiv += resource.action.map((item) => {
                let title = item.title || "";
                if (item.prefix) {
                    title = item.prefix + " " + title;
                }
                let description = item.description || "";
                let textEquivalent = item.textEquivalent || "";
                let code = displayStringFromFHIRCodeableConcept(item.code) || "";
                let type = displayStringFromFHIRCodeableConcept(item.type) || "";
                let definition = item.definitionCanonical || item.definitionUri || "";
                let timing = displayStringFromFHIRTiming(item.timingTiming) ||
                    displayStringFromFHIRQuantity(item.timingAge) ||
                    displayStringFromFHIRRange(item.timingRange) ||
                    displayStringFromFHIRQuantity(item.timingDuration) || "";
                return "<p><b>Action (" + title + "): </b>" + textEquivalent + " " + description + " " + code + " " + type + " " + definition + " " + timing + "</p><br/>"
            }).join("");
        }
        if (resource.asNeededCodeableConcept) {
            innerDiv += "<p><b>As needed for: </b>" + displayStringFromFHIRCodeableConcept(resource.asNeededCodeableConcept) + "</p><br/>";
        }
        if (resource.asNeededBoolean !== undefined) {
            innerDiv += "<p><b>As needed: </b>" + displayStringFromFHIRBoolean(resource.asNeededBoolean) + "</p><br/>";
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + innerDiv + "</div>" };
}

const PlanDefinitionActionEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  return <p>Data entry form not yet created.</p>
})

const PlanDefinitionActorEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  return <p>Data entry form not yet created.</p>
})

const PlanDefinitionGoalEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  return <p>Data entry form not yet created.</p>
})

const PlanDefinitionDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [planDefinitionResourceState, setPlanDefinitionResourceState] = useState({});
    const [subjectDatatypeState, setSubjectDatatypeState] = useState('none');
    const [asNeededDatatypeState, setAsNeededDatatypeState] = useState('none');

    useEffect(() => {
        if (resourceJson) {
            setPlanDefinitionResourceState(resourceJson);
            dispatch(changeTopNavigationElements(["Summary", "Title", "Goal", "Actor", "Action"]));
            dispatch(changeTabMenu(["Text View", "JSON View", "Usage View"]));
            if (resourceJson.subjectReference) { setSubjectDatatypeState("Reference"); }
            else if (resourceJson.subjectCodeableConcept) { setSubjectDatatypeState("CodeableConcept"); }
            else if (resourceJson.subjectCanonical) { setSubjectDatatypeState("canonical"); }
            if (resourceJson.asNeededCodeableConcept) { setAsNeededDatatypeState("CodeableConcept"); }
            else if (resourceJson.asNeededBoolean !== undefined) { setAsNeededDatatypeState("boolean"); }
        }
    }, [resourceJson]);

    useEffect((() => {
        if (subjectDatatypeState === 'CodeableConcept') {
            setPlanDefinitionResourceState(prevState => { return { ...prevState, 'subjectReference': null, 'subjectCanonical': null } })
        }
        if (subjectDatatypeState === 'Reference') {
            setPlanDefinitionResourceState(prevState => { return { ...prevState, 'subjectCodeableConcept': null, 'subjectCanonical': null } })
        }
        if (subjectDatatypeState === 'canonical') {
            setPlanDefinitionResourceState(prevState => { return { ...prevState, 'subjectReference': null, 'subjectCodeableConcept': null } })
        }
    }), [subjectDatatypeState]);

    useEffect((() => {
        if (asNeededDatatypeState === 'boolean') {
            setPlanDefinitionResourceState(prevState => { return { ...prevState, 'asNeededCodeableConcept': null } })
        }
        if (asNeededDatatypeState === 'CodeableConcept') {
            setPlanDefinitionResourceState(prevState => { return { ...prevState, 'asNeededBoolean': null } })
        }
    }), [asNeededDatatypeState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(planDefinitionResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = planDefinitionResourceState.title;
            newResourceDetail.displayTitle = planDefinitionResourceState.title ||
                planDefinitionResourceState.name || newResourceDetail.displayTitle;
            if (planDefinitionResourceState.title !== undefined &&
                previousFinalValue !== planDefinitionResourceState.title) {
                console.log("resourceUpdate for PlanDefinition.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    planDefinitionResourceState.title);
            }
        }
    }, [planDefinitionResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = planDefinitionResourceState.name;
            newResourceDetail.displayTitle = planDefinitionResourceState.title ||
                planDefinitionResourceState.name || newResourceDetail.displayTitle;
            if (planDefinitionResourceState.name !== undefined &&
                previousFinalValue !== planDefinitionResourceState.name) {
                console.log("resourceUpdate for PlanDefinition.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    planDefinitionResourceState.name);
            }
        }
    }, [planDefinitionResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "subtitle", "string", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.subtitle]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "type", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "goal", "ArrayOfPlanDefinitionGoal", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.goal]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "actor", "ArrayOfPlanDefinitionActor", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.actor]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "subjectCodeableConcept", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.subjectCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "subjectReference", "Reference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.subjectReference]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "subjectCanonical", "canonical", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.subjectCanonical]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "action", "ArrayOfPlanDefinitionAction", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.action]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "asNeededCodeableConcept", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.asNeededCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(planDefinitionResourceState, "asNeededBoolean", "boolean", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [planDefinitionResourceState.asNeededBoolean]);

    return <div>
        <div style={{ marginTop: "12px" }}>
            <h3 id="summary">Summary</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                            generatedNarrative={generateNarrativeForPlanDefinitionResource(planDefinitionResourceState)} startCollapsed
                            startingValue={planDefinitionResourceState.text} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {(resourceJson.text?.div && resourceJson.text.status && resourceJson.text.status !== "empty") ?
                            <DisplayFromFHIRXhtml xhtml={resourceJson.text.div} />
                            : <p>[Narrative summary not generated.]</p>}
                    </>
                }
            </div>
            <h3 id="title">Title</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <StringEntry elementName='title' fieldLabel='Title'
                            startingValue={planDefinitionResourceState.title} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.title && <p><b>Title: </b>{resourceJson.title}</p>}
                    </>
                }
                {editMode ?
                    <>
                        <StringEntry elementName='subtitle' fieldLabel='Subtitle'
                            startingValue={planDefinitionResourceState.subtitle} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.subtitle && <p><b>Subtitle: </b>{resourceJson.subtitle}</p>}
                    </>
                }
                {editMode ?
                    <>
                        <StringEntry elementName='name' fieldLabel='Name'
                            startingValue={planDefinitionResourceState.name} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.name && <p><b>Name: </b>{resourceJson.name}</p>}
                    </>
                }
                {resourceJson.description && <div>
                    <b>Description: </b><DisplayFromFHIRMarkdown markdown={resourceJson.description} />
                    {editMode ?
                        <p>(Description content can be edited in the Metadata.)</p> :
                        <><br /><br /></>}
                </div>}
                {editMode ?
                    <>
                        <CodeableConceptEntry elementName='type'
                            fieldLabel='Type' startingValue={planDefinitionResourceState.type || null}
                            valueSet={planDefinitionDotTypeValueSet} startCollapsed
                            setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.type &&
                            <p><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={resourceJson.type} /></p>}
                    </>
                }
            </div>
            <h3 id="goal">Goal</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <ArrayEntry datatype='PlanDefinitionGoal' elementName='goal' startCollapsed
                            fieldLabel='Goal' startingValue={planDefinitionResourceState.goal} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {(Array.isArray(resourceJson.goal) && resourceJson.goal.length > 0)
                            ?
                            <div style={{ marginLeft: "24px" }}>
                                {resourceJson.goal.map((goal, goalIndex) => {
                                    return <div key={goalIndex}>
                                        <p><b>Goal entry {goalIndex + 1}</b></p>
                                        <div style={{ marginLeft: "24px" }}>
                                            {goal.category && <span><b>Category: </b><DisplayFromFHIRCodeableConcept codeableConcept={goal.category} /></span>}
                                            {goal.description && <span><br /><b>Description: </b><DisplayFromFHIRCodeableConcept codeableConcept={goal.description} /></span>}
                                            {goal.priority && <span><br /><b>Priority: </b><DisplayFromFHIRCodeableConcept codeableConcept={goal.priority} /></span>}
                                            {goal.start && <span><br /><b>Start: </b><DisplayFromFHIRCodeableConcept codeableConcept={goal.start} /></span>}
                                            {Array.isArray(goal.addresses) && goal.addresses.map((addresses, addressesIndex) => {
                                                return <span key={addressesIndex}><br /><b>The goal addresses: </b><DisplayFromFHIRCodeableConcept codeableConcept={addresses} /></span>
                                            })}
                                            {Array.isArray(goal.documentation) && goal.documentation.map((documentation, documentationIndex) => {
                                                return <div key={documentationIndex}>
                                                    <p><b>Supporting documentation: </b></p>
                                                    <DisplayFromFHIRRelatedArtifact relatedArtifact={documentation} /></div>
                                            })}
                                            {Array.isArray(goal.target) && goal.target.map((target, targetIndex) => {
                                                return <div key={targetIndex}>
                                                    <p><b>Goal Target {targetIndex + 1}</b></p>
                                                    <div style={{ marginLeft: "24px" }}>
                                                        {target.measure && <span><b>Measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={target.measure} /></span>}
                                                        {target.detailQuantity && <p><b>Value: </b>{displayStringFromFHIRQuantity(target.detailQuantity)}</p>}
                                                        {target.detailRange && <p><b>Value: </b>{displayStringFromFHIRRange(target.detailRange)}</p>}
                                                        {target.detailCodeableConcept && <span><br /><b>Value: </b><DisplayFromFHIRCodeableConcept codeableConcept={target.detailCodeableConcept} /></span>}
                                                        {target.detailString && <p><b>Value: </b>{target.detailString}</p>}
                                                        {target.detailBoolean && <p><b>Value: </b>{displayStringFromFHIRBoolean(target.detailBoolean)}</p>}
                                                        {target.detailInteger && <p><b>Value: </b>{target.detailInteger.toString()}</p>}
                                                        {target.detailRatio && <p><b>Value: </b>{getRatioDisplay(target.detailRatio)}</p>}
                                                    </div>
                                                </div>
                                            })}
                                        </div>
                                    </div>
                                })}
                            </div>
                            :
                            <p>No goal data.</p>
                        }
                    </>
                }
            </div>
            <h3 id="actor">Actor</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <ArrayEntry datatype='PlanDefinitionActor' elementName='actor' startCollapsed
                            fieldLabel='Actor' startingValue={planDefinitionResourceState.actor} setResourceState={setPlanDefinitionResourceState} />
                    </>
                    :
                    <>
                        {(Array.isArray(resourceJson.actor) && resourceJson.actor.length > 0)
                            ?
                            <div style={{ marginLeft: "24px" }}>
                                {resourceJson.actor.map((actor, actorIndex) => {
                                    return <div key={actorIndex}>
                                        <p><b>Actor entry {actorIndex + 1}</b></p>
                                        <div style={{ marginLeft: "24px" }}>
                                            {actor.title && <p><b>Actor title: </b>{actor.title}</p>}
                                            {actor.description && <span><b>Actor description: </b><DisplayFromFHIRMarkdown markdown={actor.description} /></span>}
                                            {Array.isArray(actor.option) && actor.option.map((option, optionIndex) => {
                                                return <div key={optionIndex}>
                                                    <p><b>Actor type {optionIndex + 1}</b></p>
                                                    <div style={{ marginLeft: "24px" }}>
                                                        {option.type && <p><b>Type: </b>{option.type}</p>}
                                                        {option.typeCanonical && <p><b>Type: </b><DisplayFromFHIRUri uri={option.typeCanonical} /></p>}
                                                        {option.typeReference && <p><b>Type: </b><DisplayFromFHIRReference reference={option.typeReference} /></p>}
                                                        {option.role && <span><b>Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={option.role} /></span>}
                                                    </div>
                                                </div>
                                            })}
                                        </div>
                                    </div>
                                })}
                            </div>
                            :
                            <p>No actor data.</p>
                        }
                    </>
                }
                {editMode ?
                    <>
                        <p><b>Subject:</b></p>
                        <div style={{ marginLeft: "24px" }}>
                            <DatatypeSelector elementXName='subject[x]' allowedDatatypes={planDefinitionDotSubjectAllowedDatatypes}
                                datatypeState={subjectDatatypeState} setDatatypeState={setSubjectDatatypeState} />
                            {(subjectDatatypeState === 'CodeableConcept') &&
                                <CodeableConceptEntry elementName='subjectCodeableConcept' startCollapsed
                                    fieldLabel='Subject (as CodeableConcept)' startingValue={planDefinitionResourceState.subjectCodeableConcept || null}
                                    valueSet={planDefinitionDotSubjectCodeableConceptValueSet} setResourceState={setPlanDefinitionResourceState} />}
                            {(subjectDatatypeState === 'Reference') &&
                                <ReferenceEntry elementName='subjectReference' fieldLabel='Subject (as Reference)'
                                    referencedResourceTypes={planDefinitionDotSubjectReferenceResourceTypes} startCollapsed
                                    startingValue={planDefinitionResourceState.subjectReference || null} setResourceState={setPlanDefinitionResourceState} />}
                            {(subjectDatatypeState === 'canonical') &&
                                <UriEntry elementName='subjectCanonical' fieldLabel='Subject (as Canonical URI)'
                                    startingValue={planDefinitionResourceState.subjectCanonical || null} setResourceState={setPlanDefinitionResourceState} />}
                        </div>
                    </>
                    :
                    <>
                        {resourceJson.subjectCodeableConcept && <span><b>Subject: </b><DisplayFromFHIRCodeableConcept codeableConcept={resourceJson.subjectCodeableConcept} /></span>}
                        {resourceJson.subjectReference && <p><b>Subject: </b><DisplayFromFHIRReference reference={resourceJson.subjectReference} /></p>}
                        {resourceJson.subjectCanonical && <p><b>Subject: </b><DisplayFromFHIRUri uri={resourceJson.subjectCanonical} /></p>}
                    </>
                }
            </div>
            <h3 id="action">Action</h3>
            <div style={{ marginLeft: "24px" }}>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <ArrayEntry datatype='PlanDefinitionAction' elementName='action' startCollapsed
                                fieldLabel='Action' startingValue={planDefinitionResourceState.action} setResourceState={setPlanDefinitionResourceState} />
                        </>
                        :
                        <>
                            {(Array.isArray(resourceJson.action) && resourceJson.action.length > 0)
                                ?
                                <div style={{ marginLeft: "24px" }}>
                                    {resourceJson.action.map((action, actionIndex) => {
                                        return <div key={actionIndex}>
                                            <p><b>Action entry {actionIndex + 1}</b></p>
                                            <div style={{ marginLeft: "24px" }}>
                                                {action.linkId && <p><b>Action id: </b>{action.linkId}</p>}
                                                {(action.prefix || action.title) && <p><b>Action title: </b>{action.prefix} {action.title}</p>}
                                                {action.description && <span><b>Action description: </b><DisplayFromFHIRMarkdown markdown={action.description} /></span>}
                                                {action.textEquivalent && <span><b>Action text equivalent: </b><DisplayFromFHIRMarkdown markdown={action.textEquivalent} /></span>}
                                                {action.priority && <p><b>Action priority: </b>{action.priority}</p>}
                                                {action.code && <p><b>Action meaning: </b><DisplayFromFHIRCodeableConcept codeableConcept={action.code} /></p>}
                                                {Array.isArray(action.reason) && action.reason.map((reason, reasonIndex) => {
                                                    return <span key={reasonIndex}><br /><b>Reason: </b><DisplayFromFHIRCodeableConcept codeableConcept={reason} /></span>
                                                })}
                                                {Array.isArray(action.documentation) && action.documentation.map((documentation, documentationIndex) => {
                                                    return <div key={documentationIndex}>
                                                        <p><b>Supporting documentation: </b></p>
                                                        <DisplayFromFHIRRelatedArtifact relatedArtifact={documentation} /></div>
                                                })}
                                                {action.goalId?.length > 0 && <p><b>Goals: </b>{action.goalId.join(', ')}</p>}
                                                {action.subjectCodeableConcept && <span><b>Subject: </b><DisplayFromFHIRCodeableConcept codeableConcept={action.subjectCodeableConcept} /></span>}
                                                {action.subjectReference && <p><b>Subject: </b><DisplayFromFHIRReference reference={action.subjectReference} /></p>}
                                                {action.subjectCanonical && <p><b>Subject: </b><DisplayFromFHIRUri uri={action.subjectCanonical} /></p>}
                                                {Array.isArray(action.trigger) && action.trigger.map((trigger, triggerIndex) => {
                                                    return <div key={triggerIndex}>
                                                        <p><b>Trigger {triggerIndex + 1}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            {trigger.type && <p><b>Type: </b>{trigger.type}</p>}
                                                            {trigger.name && <p><b>Name: </b>{trigger.name}</p>}
                                                            {trigger.code && <span><b>Definition: </b><DisplayFromFHIRCodeableConcept codeableConcept={trigger.code} /></span>}
                                                            {trigger.subscriptionTopic && <p><b>Event: </b><DisplayFromFHIRUri uri={trigger.subscriptionTopic} /></p>}
                                                            {trigger.timingTiming && <p><b>Timing: </b>{displayStringFromFHIRTiming(trigger.timingTiming)}</p>}
                                                            {trigger.timingReference && <p><b>Timing: </b><DisplayFromFHIRReference reference={trigger.timingReference} /></p>}
                                                            {trigger.timingDate && <p><b>Timing: </b>{getDateDisplay(trigger.timingDate)}</p>}
                                                            {trigger.timingDateTime && <p><b>Timing: </b>{getDateTimeDisplay(trigger.timingDateTime)}</p>}
                                                            {trigger.data && <p>JSON data includes triggering data requirement.</p>}
                                                            {trigger.condition && <p><b>Condition: </b><DisplayFromFHIRExpression expression={trigger.condition} /></p>}
                                                        </div>
                                                    </div>
                                                })}
                                                {Array.isArray(action.condition) && action.condition.map((condition, conditionIndex) => {
                                                    return <div key={conditionIndex}>
                                                        <p><b>Condition {conditionIndex + 1}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            {condition.kind && <p><b>Kind: </b>{condition.kind}</p>}
                                                            {condition.expression && <p><b>Expression: </b><DisplayFromFHIRExpression expression={condition.expression} /></p>}
                                                        </div>
                                                    </div>
                                                })}
                                                {Array.isArray(action.input) && action.input.map((input, inputIndex) => {
                                                    return <div key={inputIndex}>
                                                        <p><b>Input {inputIndex + 1}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            {input.title && <p><b>Title: </b>{input.title}</p>}
                                                            {input.requirement && <p>JSON data includes input data requirement.</p>}
                                                            {input.relatedData && <p><b>Data: </b>{input.relatedData}</p>}
                                                        </div>
                                                    </div>
                                                })}
                                                {Array.isArray(action.output) && action.output.map((output, outputIndex) => {
                                                    return <div key={outputIndex}>
                                                        <p><b>Output {outputIndex + 1}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            {output.title && <p><b>Title: </b>{output.title}</p>}
                                                            {output.requirement && <p>JSON data includes output data requirement.</p>}
                                                            {output.relatedData && <p><b>Data: </b>{output.relatedData}</p>}
                                                        </div>
                                                    </div>
                                                })}
                                                {Array.isArray(action.relatedAction) && action.relatedAction.map((relatedAction, relatedActionIndex) => {
                                                    return <div key={relatedActionIndex}>
                                                        <p><b>Related Action {relatedActionIndex + 1}</b></p>
                                                        <div style={{ marginLeft: "24px" }}>
                                                            {relatedAction.targetId && <p><b>Id: </b>{relatedAction.targetId}</p>}
                                                            {relatedAction.relationship && <p><b>Start timing: </b>{relatedAction.relationship}</p>}
                                                            {relatedAction.endRelationship && <p><b>End timing: </b>{relatedAction.endRelationship}</p>}
                                                            {relatedAction.offsetDuration && <p><b>Offset: </b>{displayStringFromFHIRQuantity(relatedAction.offsetDuration)}</p>}
                                                            {relatedAction.offsetRange && <p><b>Offset: </b>{displayStringFromFHIRRange(relatedAction.offsetRange)}</p>}
                                                        </div>
                                                    </div>
                                                })}
                                                {action.timingTiming && <p><b>Timing: </b>{displayStringFromFHIRTiming(action.timingTiming)}</p>}
                                                {action.timingAge && <p><b>Timing: </b>{displayStringFromFHIRQuantity(action.timingAge)}</p>}
                                                {action.timingRange && <p><b>Timing: </b>{displayStringFromFHIRRange(action.timingRange)}</p>}
                                                {action.timingDuration && <p><b>Timing: </b>{displayStringFromFHIRQuantity(action.timingDuration)}</p>}
                                                {action.location && <p><b>Location: </b><DisplayFromFHIRCodeableReference codeableReference={action.location} /></p>}
                                                {(Array.isArray(action.participant) && action.participant.length > 0) && <div>
                                                    <p>{action.participant.length === 1 ? <b>Participant: </b> : <b>Participants: </b>}</p>
                                                    <div style={{ marginLeft: "24px" }}>
                                                        {action.participant.map((participant, participantIndex) => {
                                                            return <div key={participantIndex}>
                                                                <p><b>Participant entry {participantIndex + 1}</b></p>
                                                                <div style={{ marginLeft: "24px" }}>
                                                                    {participant.actorId && <p><b>Participant actor id: </b>{participant.actorId}</p>}
                                                                    {participant.type && <p><b>Participant type: </b>{participant.type}</p>}
                                                                    {participant.typeCanonical && <p><b>Participant type canonical: </b><DisplayFromFHIRUri uri={participant.typeCanonical} /></p>}
                                                                    {participant.typeReference && <p><b>Participant type reference: </b><DisplayFromFHIRReference reference={participant.typeReference} /></p>}
                                                                    {participant.role && <span><b>Participant role: </b><DisplayFromFHIRCodeableConcept codeableConcept={participant.role} /></span>}
                                                                    {participant.function && <span><b>Participant function: </b><DisplayFromFHIRCodeableConcept codeableConcept={participant.function} /></span>}
                                                                </div>
                                                            </div>
                                                        })}
                                                    </div>
                                                </div>
                                                }
                                                {action.type && <span><b>Action type: </b><DisplayFromFHIRCodeableConcept codeableConcept={action.type} /></span>}
                                                {action.groupingBehavior && <p><b>Grouping Behavior: </b>{action.groupingBehavior}</p>}
                                                {action.selectionBehavior && <p><b>Selection Behavior: </b>{action.selectionBehavior}</p>}
                                                {action.requiredBehavior && <p><b>Required Behavior: </b>{action.requiredBehavior}</p>}
                                                {action.precheckBehavior && <p><b>Precheck Behavior: </b>{action.precheckBehavior}</p>}
                                                {action.cardinalityBehavior && <p><b>Cardinality Behavior: </b>{action.cardinalityBehavior}</p>}
                                                {action.definitionCanonical && <p><b>Action Definition: </b><DisplayFromFHIRUri uri={action.definitionCanonical} /></p>}
                                                {action.definitionUri && <p><b>Action Definition: </b><DisplayFromFHIRUri uri={action.definitionUri} /></p>}
                                                {action.transform && <p><b>Transform to apply: </b><DisplayFromFHIRUri uri={action.transform} /></p>}
                                                {(Array.isArray(action.dynamicValue) && action.dynamicValue.length > 0) && <div>
                                                    <p>{action.dynamicValue.length === 1 ? <b>Dynamic Value: </b> : <b>Dynamic Values: </b>}</p>
                                                    <div style={{ marginLeft: "24px" }}>
                                                        {action.dynamicValue.map((dynamicValue, dynamicValueIndex) => {
                                                            return <div key={dynamicValueIndex}>
                                                                {dynamicValue.path && <p><b>Path: </b>{dynamicValue.path}</p>}
                                                                {dynamicValue.expression &&
                                                                    <div><b>Expression: </b><DisplayFromFHIRExpression expression={dynamicValue.expression} /></div>}
                                                                <br />
                                                            </div>
                                                        })}
                                                    </div>
                                                </div>
                                                }
                                                {action.action && <p>JSON data includes sub-actions.</p>}
                                            </div>
                                        </div>
                                    })}
                                </div>
                                :
                                <p>No action data.</p>
                            }
                        </>
                    }
                    {editMode ?
                        <>
                            <p><b>As Needed:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='asNeeded[x]' allowedDatatypes={planDefinitionDotAsNeededAllowedDatatypes}
                                    datatypeState={asNeededDatatypeState} setDatatypeState={setAsNeededDatatypeState} />
                                {(asNeededDatatypeState === 'boolean') &&
                                    <BooleanEntry elementName='asNeededBoolean' fieldLabel='As needed'
                                        startingValue={planDefinitionResourceState.asNeededBoolean ?? null} setResourceState={setPlanDefinitionResourceState}
                                        storeFalse={true} />}
                                {(asNeededDatatypeState === 'CodeableConcept') &&
                                    <CodeableConceptEntry elementName='asNeededCodeableConcept' startCollapsed
                                        fieldLabel='As needed (reason)' startingValue={planDefinitionResourceState.asNeededCodeableConcept || null}
                                        systemChoices={planDefinitionDotAsNeededCodeableConceptSystemChoices} systemChoicesOpen
                                        setResourceState={setPlanDefinitionResourceState} />}
                            </div>
                        </>
                        :
                        <>
                            {(resourceJson.asNeededBoolean !== undefined || resourceJson.asNeededCodeableConcept) &&
                                <p><b>As Needed:</b></p>}
                            <div style={{ marginLeft: "24px" }}>
                                {resourceJson.asNeededBoolean !== undefined &&
                                    <p><b>As needed: </b>{displayStringFromFHIRBoolean(resourceJson.asNeededBoolean)}</p>}
                                {resourceJson.asNeededCodeableConcept &&
                                    <span><b>As needed for: </b><DisplayFromFHIRCodeableConcept codeableConcept={resourceJson.asNeededCodeableConcept} /></span>}
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
    </div>
}

export default PlanDefinitionDisplay;
export { PlanDefinitionActionEntry, PlanDefinitionActorEntry, PlanDefinitionGoalEntry };